import { useZero } from "@rocicorp/zero/react";
import {
  type AIProviderId,
  managedAIProviderIdForModel,
  MANAGED_AI_PROVIDER_ID,
  resolveAIProviderId,
} from "../../shared/aiProviders";
import { sharedMutators } from "../../shared/mutators";
import { queries } from "../../shared/queries";
import { useManagedAIProvider } from "./useManagedAIProvider";
import { DEFERRED, useQuery } from "./useQuery";

export type ModelScope =
  | { type: "branch"; branchId: string }
  | { type: "personal" };

// Resolves the model a given chat scope will actually run with, for any user
// (the model list is readable by everyone). A scope follows the tenant default
// until the user pins a choice: their stored id, the managed sentinel, or — when
// the pinned config no longer exists — back to the default.
export function useScopedModel(
  scope: ModelScope,
  options?: { enabled?: boolean; chatModel?: string | null }
) {
  // Deferred so the composer's model queries don't add to the concurrent-query count during the cold-load list fetch.
  const { enabled = true, chatModel } = options ?? {};
  const z = useZero();
  const [configsResult] = useQuery(
    enabled ? queries.aiConfigs() : undefined,
    DEFERRED
  );
  const configs = configsResult ?? [];
  const [preferences] = useQuery(queries.userPreferences());
  const [branchPref] = useQuery(
    enabled
      ? queries.branchModelPreference({
          branchId: scope.type === "branch" ? scope.branchId : "",
        })
      : undefined,
    DEFERRED
  );

  const { availableProvider: managedProvider } = useManagedAIProvider();
  const activeConfig = configs.find((c) => c.active) ?? null;
  const tenantDefaultKey: string | null = activeConfig
    ? activeConfig.id
    : managedProvider
      ? MANAGED_AI_PROVIDER_ID
      : null;

  const prefValue =
    (scope.type === "branch"
      ? branchPref?.aiProviderId
      : preferences?.aiProviderId) ?? null;

  const effectiveKey: string | null =
    prefValue === MANAGED_AI_PROVIDER_ID
      ? managedProvider
        ? MANAGED_AI_PROVIDER_ID
        : tenantDefaultKey
      : prefValue &&
          configs.some((c) => c.id === prefValue && c.disabledAt == null)
        ? prefValue
        : tenantDefaultKey;

  const effectiveConfig =
    effectiveKey && effectiveKey !== MANAGED_AI_PROVIDER_ID
      ? (configs.find((c) => c.id === effectiveKey) ?? null)
      : null;

  const providerId: AIProviderId = effectiveConfig
    ? resolveAIProviderId(effectiveConfig)
    : effectiveKey === MANAGED_AI_PROVIDER_ID && managedProvider
      ? managedAIProviderIdForModel(chatModel)
      : (managedProvider ?? resolveAIProviderId());

  const setModel = (aiProviderId: string | null) => {
    if (scope.type === "branch") {
      z.mutate(
        sharedMutators.setBranchModelPreference({
          branchId: scope.branchId,
          aiProviderId,
        })
      );
    } else {
      z.mutate(sharedMutators.setUserPreference({ aiProviderId }));
    }
  };

  return {
    managedProvider,
    configs,
    tenantDefaultKey,
    effectiveKey,
    effectiveConfig,
    providerId,
    setModel,
  };
}

export type ScopedModel = ReturnType<typeof useScopedModel>;

// The tenant default provider, for contexts with no per-scope model (e.g.
// starting a new workflow). Callers pass the result to useFileDropzone.
export function useTenantDefaultProviderId(options?: {
  enabled?: boolean;
  chatModel?: string | null;
}): AIProviderId {
  const { enabled = true, chatModel } = options ?? {};
  const { availableProvider } = useManagedAIProvider();
  const [activeProvider] = useQuery(
    enabled ? queries.activeAIProvider() : undefined,
    DEFERRED
  );
  return activeProvider
    ? resolveAIProviderId(activeProvider)
    : availableProvider
      ? managedAIProviderIdForModel(chatModel)
      : "anthropic";
}
